/**
 * @file: mediaLayout.ts
 *
 * Pure layout/format helpers shared by the media, file and audio renderers.
 */

/** Drop the blurhash fragment so a bare URL is opened/previewed. */
export const stripHash = (url: string): string => url.split('#')[0];

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatBytes(n: number): string {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Constrain a media item to a display box while keeping its aspect ratio. */
export function mediaBox(
  width: number | undefined,
  height: number | undefined,
  rpx: (n: number) => number,
): { width: number; height: number } {
  const maxW = rpx(200);
  const maxH = rpx(260);
  const minSide = rpx(80);
  if (!width || !height) return { width: rpx(160), height: rpx(160) };
  const ratio = width / height;
  let w = maxW;
  let h = w / ratio;
  if (h > maxH) {
    h = maxH;
    w = h * ratio;
  }
  return { width: Math.max(minSide, w), height: Math.max(minSide, h) };
}

/** Extension-derived file badge: a short label + a type-keyed color. */
export function fileTypeBadge(filename: string | undefined): {
  label: string;
  color: string;
} {
  const ext = (filename?.split('.').pop() ?? '').toLowerCase();
  switch (ext) {
    case 'pdf':
      return { label: 'PDF', color: '#E2574C' };
    case 'doc':
    case 'docx':
      return { label: 'DOC', color: '#2B7CD3' };
    case 'xls':
    case 'xlsx':
    case 'csv':
      return { label: 'XLS', color: '#21A366' };
    case 'ppt':
    case 'pptx':
      return { label: 'PPT', color: '#D24726' };
    case 'zip':
    case 'rar':
    case '7z':
      return { label: 'ZIP', color: '#F59E0B' };
    case 'txt':
      return { label: 'TXT', color: '#64748B' };
    default:
      return {
        label: ext ? ext.slice(0, 4).toUpperCase() : '文件',
        color: '#64748B',
      };
  }
}
